import { useState } from 'react'
import PropTypes from 'prop-types'
import { PencilSquareIcon, TrashIcon, QrCodeIcon, PrinterIcon } from '@heroicons/react/24/outline'
import 'styles/TableComponent.css' // replace with tailwind

const ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 50]

const TableComponent = ({
  tableData,
  handleEdit = null,
  handleDelete = null,
  handleQRClick = null,
  handlePrintClick = null,
  defaultSortColumn = ''
}) => {
  if (!tableData || !Array.isArray(tableData.columns) || !Array.isArray(tableData.rows)) {
    return <div>No data</div>
  }

  const [selectedRows, setSelectedRows] = useState([])
  const hasCheckbox = tableData.hasCheckbox || false
  const hasActions = typeof handleEdit === 'function' || typeof handleDelete === 'function' || typeof handleQRClick === 'function'

  const [state, setState] = useState({
    columnName: defaultSortColumn,
    orderBy: 'asc'
  })

  // Separate totals row from regular data
  const regularRows = tableData.rows.filter(row => !row?.isTotal)
  const totalsRows = tableData.rows.filter(row => row?.isTotal)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const totalRows = regularRows.length
  const totalPages = Math.ceil(totalRows / rowsPerPage)

  // handle sort by column
  const handleSort = (key) => {
    const direction = state.columnName === key ? (state.orderBy.toLowerCase() === 'asc' ? 'desc' : 'asc') : 'asc'
    setState(s => ({ ...s, columnName: key, orderBy: direction }))
    setCurrentPage(1) // Reset to first page on sort
  }

  const getValue = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc?.[part], obj)
  }

  const getSortedRows = () => {
    // If no column is selected for sorting, return original order
    if (!state.columnName) return regularRows

    return [...regularRows].sort((a, b) => {
      const aVal = getValue(a, state.columnName)
      const bVal = getValue(b, state.columnName)

      if (aVal === undefined || bVal === undefined) return 0

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return state.orderBy === 'asc' ? aVal - bVal : bVal - aVal
      }

      const aNum = Number(aVal)
      const bNum = Number(bVal)
      if (!isNaN(aNum) && !isNaN(bNum) && aVal !== '' && bVal !== '') {
        return state.orderBy === 'asc' ? aNum - bNum : bNum - aNum
      }

      return state.orderBy === 'asc'
        ? String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base' })
        : String(bVal).localeCompare(String(aVal), undefined, { sensitivity: 'base' })
    })
  }

  // Pagination: get rows for current page
  const getPaginatedRows = () => {
    const sortedRows = getSortedRows()
    const startIdx = (currentPage - 1) * rowsPerPage
    return sortedRows.slice(startIdx, startIdx + rowsPerPage)
  }

  // handle selecting individual rows
  const handleRowSelect = (rowIndex) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(rowIndex) ? prevSelected.filter((index) => index !== rowIndex) : [...prevSelected, rowIndex]
    )
  }

  // handle selecting all rows
  const handleSelectAll = () => {
    const paginatedRows = getPaginatedRows()
    const paginatedIndexes = paginatedRows.map((_, idx) => (currentPage - 1) * rowsPerPage + idx)
    const allSelected = paginatedIndexes.every(idx => selectedRows.includes(idx))
    setSelectedRows(allSelected
      ? selectedRows.filter(idx => !paginatedIndexes.includes(idx))
      : [...selectedRows, ...paginatedIndexes.filter(idx => !selectedRows.includes(idx))]
    )
  }

  // render checkbox in header only if hasCheckbox is true
  const renderCheckboxHead = () => (
    <th>
      <input
        type='checkbox'
        onChange={handleSelectAll}
        checked={
          getPaginatedRows().length > 0 &&
          getPaginatedRows().every((_, idx) => selectedRows.includes((currentPage - 1) * rowsPerPage + idx))
        }
      />
    </th>
  )

  // render checkbox in column only if hasCheckbox is true
  const renderCheckboxBody = (rowIndex) => (
    <td>
      <input
        type='checkbox'
        checked={selectedRows.includes(rowIndex)}
        onChange={() => handleRowSelect(rowIndex)}
      />
    </td>
  )

  // render cell content, if array paints as list or direct value
  const renderCellContent = (cellData) => {
    if (Array.isArray(cellData)) {
      return (
        <ul style={{ paddingLeft: '16px', margin: 0 }}>
          {cellData.length > 0 ? cellData.map((item, index) => <li key={index}>{item}</li>) : <li>-</li>}
        </ul>
      )
    }
    return cellData ?? '-'
  }

  // renders table data
  const renderTableData = (row) =>
    tableData.columns.map((col, colIndex) => {
      const value = row?.[col.key]

      if (col.key === 'actions') {
        if (typeof col.cell === 'function') {
          return (
            <td key={colIndex}>
              {col.cell(value, row)}
            </td>
          )
        }

        if (hasActions) {
          return (
            <td key={colIndex}>
              {handleEdit && (
                <button onClick={() => handleEdit(row)} title='Edit'>
                  <PencilSquareIcon className='w-5 h-5 text-blue-500' />
                </button>
              )}
              {handleDelete && (
                <button onClick={() => handleDelete(row)} title='Delete'>
                  <TrashIcon className='w-5 h-5 text-red-500' />
                </button>
              )}
              {handleQRClick && (
                <button onClick={() => handleQRClick(row)} title='QR Code'>
                  <QrCodeIcon className='w-5 h-5 text-gray-600' />
                </button>
              )}
              {handlePrintClick && (
                <button onClick={() => handlePrintClick(row)} title='Print'>
                  <PrinterIcon className='w-5 h-5 text-gray-600' />
                </button>
              )}
            </td>
          )
        }

        return <td key={colIndex}>-</td>
      }

      return (
        <td key={colIndex}>
          {col.cell ? col.cell(value, row) : renderCellContent(value)}
        </td>
      )
    })

  // Pagination controls with rows per page selector
  const renderPagination = () => (
    <div className='flex items-center justify-start mt-2 gap-2 text-sm'>
      <span>Rows:</span>
      <select
        className='border rounded px-1 py-0.5'
        value={rowsPerPage}
        onChange={e => {
          setRowsPerPage(Number(e.target.value))
          setCurrentPage(1)
        }}
      >
        {ROWS_PER_PAGE_OPTIONS.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
      <button
        className='px-2 py-1 bg-gray-200 rounded disabled:opacity-50'
        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        disabled={currentPage === 1}
      >
        Prev
      </button>
      <span>
        Page {currentPage} / {totalPages}
      </span>
      <button
        className='px-2 py-1 bg-gray-200 rounded disabled:opacity-50'
        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages}
      >
        Next
      </button>
    </div>
  )

  return (
    <div className='overflow-x-auto max-w-5l'>
      <div className='table-container table-wrapper'>
        <table className='table w-full border-rounded-lg border border-gray-200 text-left'>
          <thead>
            <tr className='cursor-pointer text-greenPrimary hover:bg-gray-100 transition-colors duration-200'>
              {hasCheckbox && renderCheckboxHead()}
              {tableData.columns.map((col, index) => (
                <th
                  onClick={() => handleSort(col.key)}
                  key={index}
                  className='hover:bg-gray-200 transition-colors duration-150'
                >
                  {col.label} {col.key !== 'actions' && state.columnName === col.key && (<span>{state.orderBy === 'asc' ? ' ↑' : ' ↓'}</span>)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Regular data rows */}
            {getPaginatedRows().map((row, idx) => {
              const globalRowIndex = (currentPage - 1) * rowsPerPage + idx
              return row
                ? (
                  <tr key={globalRowIndex}>
                    {hasCheckbox && renderCheckboxBody(globalRowIndex)}
                    {renderTableData(row)}
                  </tr>
                  )
                : null
            })}
            {/* Totals rows - always at the bottom */}
            {totalsRows.map((row, idx) => {
              const totalsRowIndex = `totals-${idx}`
              return (
                <tr key={totalsRowIndex} className='font-bold bg-gray-100 border-t-2 border-gray-400'>
                  {hasCheckbox && <td />}
                  {renderTableData(row)}
                </tr>
              )
            })}
          </tbody>
        </table>
        {totalPages > 1 && renderPagination()}
      </div>
    </div>
  )
}

TableComponent.propTypes = {
  tableData: PropTypes.shape({
    hasCheckbox: PropTypes.bool,
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        cell: PropTypes.func
      })
    ).isRequired,
    rows: PropTypes.arrayOf(PropTypes.object).isRequired
  }).isRequired,
  handleEdit: PropTypes.func,
  handleDelete: PropTypes.func,
  handleQRClick: PropTypes.func
}

export default TableComponent
